import { ChangeDetectionStrategy, Component, ViewEncapsulation } from '@angular/core';
import { getUuid } from '@sibe/utilities/common';
import {
  HsAmbientLight,
  HsColor,
  HsDefaultMaterial,
  HsDefaultShader,
  HsDirectionalLight,
  HsGraphicObject,
  HsLightStorage,
  HsPointLight,
  HsShaderData,
  HsShaderObjectType,
  HsSpotLight,
  hsGenerateCubeGeometryData,
  hsMakeDefaultLights,
  hsMakeGeometry,
  type HsHypersceneViewer,
  type HsLight,
  type HsMaterial,
  type HsRegularGeometry,
} from '@sibe/hyperscene';
import { DemoViewerPage } from '../../classes/demo-viewer-page.class';
import { makeLitMaterial } from '../../functions/make-lit-material.function';

const CAMERA_POSITION: [number, number, number] = [2, 10, 30];
const GROUND_SIZE: number = 40;

/** A white cube on a checkerboard ground lit by any combination of ambient, directional, point and spot lights. */
@Component({
  selector: 'hyperscene-lights',
  templateUrl: './lights.component.html',
  styleUrls: ['./lights.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.Emulated,
  standalone: false,
})
export class LightsDemo extends DemoViewerPage {
  public readonly lightToolTypesMap: Map<number, string> = new Map([
    [0, 'Ambient'],
    [1, 'Directional'],
    [2, 'Point 1'],
    [3, 'Point 2'],
    [4, 'Dynamic Points'],
    [5, 'Circle of Dynamic Points'],
    [6, 'Spotlight'],
    [7, 'Dynamic Spotlight'],
    [8, 'Dynamic Colors'],
  ]);

  public selectedLightTools: boolean[] = [true, true, false, false, false];

  private readonly cubes: { object: HsGraphicObject; color: [number, number, number] }[] = [];
  private readonly dynamicSpotLight: HsSpotLight = new HsSpotLight(getUuid(), 'Spotlight');
  private dynamicPointLights: HsPointLight[] = [];
  private colorPointLights: HsPointLight[] = [];

  protected initScene(viewer: HsHypersceneViewer): void {
    const geometry: HsRegularGeometry | null = hsMakeGeometry(viewer.surface.graphics, hsGenerateCubeGeometryData());
    if (geometry === null) {
      return;
    }
    // applyLights gives every cube its lit material; until then they share one placeholder.
    const placeholder: HsMaterial = makeLitMaterial(viewer.gl, 1, 1, 1);
    this.addCube(viewer, geometry, placeholder, [1, 1, 1], [0, 2, 0], 2);
    for (let i: number = 0; i < GROUND_SIZE; i++) {
      for (let j: number = 0; j < GROUND_SIZE; j++) {
        const color: [number, number, number] = (i + j) % 2 === 1 ? [0.3, 0.3, 0.3] : [1, 1, 1];
        this.addCube(viewer, geometry, placeholder, color, [i * 2 - GROUND_SIZE, -2, j * 2 - GROUND_SIZE], 1);
      }
    }
    this.applyLights(viewer);
    viewer.camera.fov = 1;
    viewer.camera.worldMatrix.setPosition(CAMERA_POSITION);
  }

  /** Moves the dynamic lights; their shaders upload them when the next frame draws. */
  protected override animate(): boolean {
    const time: number = Date.now() * 0.003;
    this.dynamicPointLights.forEach((light: HsPointLight, index: number) => {
      const angle: number = time + (index / this.dynamicPointLights.length) * Math.PI * 2;
      light.worldMatrix.setPosition([Math.cos(angle) * 10, -15, Math.sin(angle) * 10]);
    });
    this.colorPointLights.forEach((light: HsPointLight, index: number) => {
      light.intensity = Math.pow(Math.abs(Math.sin(time + index)), 32) * 10;
    });
    this.dynamicSpotLight.worldMatrix.setPosition([Math.cos(time) * 20, -15, Math.sin(time) * 20]);
    this.dynamicSpotLight.setDirection([-Math.cos(time), 1.0, -Math.sin(time)]);
    this.dynamicSpotLight.color.copy(HsColor.makeFromHSV(Math.sin(time * 0.1) * 0.5 + 0.5, 1, 1));
    return false;
  }

  public selectLightTool(lightToolType: number): void {
    this.selectedLightTools[lightToolType] = !this.selectedLightTools[lightToolType];
    if (this.viewer !== null) {
      this.applyLights(this.viewer);
    }
  }

  /** Static lights are compiled into the shader, so a new light set needs new materials. */
  private applyLights(viewer: HsHypersceneViewer): void {
    const lightStorage: HsLightStorage = new HsLightStorage(this.makeLights());
    const shaderData: HsShaderData = new HsShaderData(HsShaderObjectType.single);
    shaderData.setLightStorage(lightStorage).addColor().addDepth().addNormals().addDiffuseLighting();
    const shader: HsDefaultShader = shaderData.makeShader();
    shader.update(viewer.gl);
    const materials: Map<string, HsDefaultMaterial<HsDefaultShader>> = new Map();
    this.cubes.forEach(({ object, color }: { object: HsGraphicObject; color: [number, number, number] }) => {
      const key: string = color.join();
      let material: HsDefaultMaterial<HsDefaultShader> | undefined = materials.get(key);
      if (material === undefined) {
        material = new HsDefaultMaterial(viewer.gl, shader, false);
        material.color.set(color[0], color[1], color[2]);
        materials.set(key, material);
      }
      object.material = material;
    });
    viewer.invalidate();
  }

  private addCube(
    viewer: HsHypersceneViewer,
    geometry: HsRegularGeometry,
    material: HsMaterial,
    color: [number, number, number],
    position: [number, number, number],
    scale: number
  ): void {
    const cube: HsGraphicObject = new HsGraphicObject(getUuid(), 'cube', geometry, material);
    cube.worldMatrix.scale([scale, scale, scale]);
    cube.worldMatrix.setPosition(position);
    cube.updateBoundingSphere();
    viewer.add(cube);
    this.cubes.push({ object: cube, color });
  }

  private makeLights(): HsLight[] {
    const lights: HsLight[] = [];
    this.dynamicPointLights = [];
    this.colorPointLights = [];
    if (this.selectedLightTools[0]) {
      const ambientLight: HsAmbientLight = new HsAmbientLight(getUuid(), 'Ambient');
      ambientLight.intensity = 0.4;
      lights.push(ambientLight);
    }
    if (this.selectedLightTools[1]) {
      lights.push(...hsMakeDefaultLights().filter((light: HsLight) => light instanceof HsDirectionalLight));
    }
    if (this.selectedLightTools[2]) {
      lights.push(this.makePointLight([1, 0.75, 0.25], [10, -10, -10]));
    }
    if (this.selectedLightTools[3]) {
      lights.push(this.makePointLight([0.25, 0.75, 1], [-10, -10, -10]));
    }
    // Both dynamic point sets share one circle, colored by their index around it.
    const dynamicPointsFalloffs: number[] = [];
    if (this.selectedLightTools[4]) {
      dynamicPointsFalloffs.push(...new Array<number>(2).fill(1));
    }
    if (this.selectedLightTools[5]) {
      dynamicPointsFalloffs.push(...new Array<number>(8).fill(1.5));
    }
    dynamicPointsFalloffs.forEach((falloff: number, index: number) => {
      const light: HsPointLight = new HsPointLight(getUuid(), 'Point');
      light.radius = 20;
      light.falloff = falloff;
      light.color.copy(HsColor.makeFromHSV(index / dynamicPointsFalloffs.length, 1, 1));
      light.dynamic = true;
      this.dynamicPointLights.push(light);
    });
    lights.push(...this.dynamicPointLights);
    if (this.selectedLightTools[6]) {
      const spotlight: HsSpotLight = new HsSpotLight(getUuid(), 'Spotlight');
      spotlight.intensity = 10.0;
      spotlight.angle = 0.1;
      spotlight.color.set(1.0, 0.9, 0.75);
      spotlight.worldMatrix.setPosition([-25, -15, -10]);
      spotlight.setDirection([1, 0.7, 0.35]);
      lights.push(spotlight);
    }
    if (this.selectedLightTools[7]) {
      this.dynamicSpotLight.intensity = 10.0;
      this.dynamicSpotLight.angle = 0.1;
      this.dynamicSpotLight.dynamic = true;
      lights.push(this.dynamicSpotLight);
    }
    if (this.selectedLightTools[8]) {
      for (let i: number = 0; i < 30; i++) {
        const light: HsPointLight = new HsPointLight(getUuid(), 'Point');
        light.radius = 6 + Math.random() * 5;
        light.falloff = 2;
        light.worldMatrix.setPosition([Math.random() * 70 - 35, -5, Math.random() * 70 - 35]);
        light.color.copy(HsColor.makeFromHSV(Math.random(), Math.random(), 1));
        light.dynamic = true;
        this.colorPointLights.push(light);
      }
      lights.push(...this.colorPointLights);
    }
    return lights;
  }

  private makePointLight(color: [number, number, number], position: [number, number, number]): HsPointLight {
    const light: HsPointLight = new HsPointLight(getUuid(), 'Point');
    light.radius = 30;
    light.color.set(color[0], color[1], color[2]);
    light.worldMatrix.setPosition(position);
    return light;
  }
}
